import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { EmptySearchResults,Pagination, Tag } from '@ekhodealer/ekho-common/rev';

import type { TableColumn } from '@common/components/Table/Table';
import type { CreditTransaction } from '@ekhodealer/ekho-common';

import { Table } from '@common/components/Table/Table';
import { formatNumberWithCommas } from '@common/lib/currency';
import { capitalizeFirstLetter, PLACEHOLDER } from '@common/lib/strings';
import { formatTimestampToDate } from '@common/lib/time';

import { useGetCredits } from '../api/useGetCredits';
import { creditsQueryParamsAtom, currentPageAtom, itemsPerPageAtom } from '../atoms/usageAtoms';

const columns: TableColumn<CreditTransaction>[] = [
	{
		id: 'date',
		title: 'Date',
		renderCell: (item: CreditTransaction) => (
			<div style={{ fontWeight: 500, color: '#333' }}>
				{formatTimestampToDate(new Date(item.dateCreated).getTime()) ?? '—'}
			</div>
		),
	},
	{
		id: 'type',
		title: 'Type',
		renderCell: (item: CreditTransaction) => {
			if (item.type === 'LOAD') {
				return (
					<Tag appearance='outline' accent={3}>
						{capitalizeFirstLetter(item.type)}
					</Tag>
				);
			} else if (item.type === 'REFUND') {
				return (
					<Tag appearance='outline' accent={1}>
						{capitalizeFirstLetter(item.type)}
					</Tag>
				);
			} else {
				return (
					<Tag appearance='outline' accent={'neutral'}>
						{capitalizeFirstLetter(item.type)}
					</Tag>
				);
			}
		},
	},
	{
		id: 'orderId',
		title: 'Order ID',
		renderCell: (item: CreditTransaction) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>
				{item?.metadata?.placedOrderId || '- -'}
			</div>
		),
	},
	{
		id: 'purchasedVehicleId',
		title: 'Purchased Vehicle ID',
		renderCell: (item: CreditTransaction) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>
				{item?.metadata?.purchasedVehicleId || '- -'}
			</div>
		),
	},
	{
		id: 'change',
		title: 'Change',
		renderCell: (item: CreditTransaction) => (
			<div
				style={{
					fontSize: '13px',
					fontWeight: 400,
				}}>
				{item.type === 'DEDUCT' ? '-' : '+'}${Math.abs(item.amount)}
			</div>
		),
	},
	{
		id: 'newBalance',
		title: 'New Balance',
		renderCell: (item: CreditTransaction) => (
			<div
				style={{
					fontSize: '13px',
					fontWeight: 500,
				}}>
				${formatNumberWithCommas(Number(item?.metadata?.currentBalance?.toFixed(2)) || 0)}
			</div>
		),
	},
	{
		id: 'description',
		title: 'Description',
		renderCell: (item: CreditTransaction) => (
			<div style={{ fontSize: '13px' }}>{item.description || PLACEHOLDER}</div>
		),
	},
];

function UsageTable() {
	// Table state atoms
	const params = useAtomValue(creditsQueryParamsAtom);
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const [itemsPerPage, setItemsPerPage] = useAtom(itemsPerPageAtom);

	// React query hook to get credits data
	const { data: transactions = [], error, isPending } = useGetCredits(params);

	const sortedTransactions = useMemo(() => {
		return [...transactions].sort((a, b) => b.dateCreated - a.dateCreated);
	}, [transactions]);

	const paginatedTransactions = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return sortedTransactions.slice(startIndex, endIndex);
	}, [sortedTransactions, currentPage, itemsPerPage]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		setItemsPerPage(newItemsPerPage);
		setCurrentPage(1); // Reset to first page when changing items per page
	};

	if (error) {
		return (
			<div style={{ color: '#ef4444', textAlign: 'center', padding: '16px' }}>
				Error loading transactions. Please try again later.
			</div>
		);
	}

	const hasEmptyResults = !isPending && sortedTransactions.length === 0;
	if (hasEmptyResults) {
		return <EmptySearchResults />;
	}

	return (
		<Table
			className='w-full credit-transactions-table'
			columns={columns}
			items={paginatedTransactions}
			getItemId={(item: CreditTransaction) => item.id}
			aria-label={isPending ? 'Loading credit transactions' : 'Credit transactions table'}
			belowTableContent={
				!hasEmptyResults &&
				!isPending && (
					<Pagination
						variant='contained'
						currentPage={currentPage}
						itemsPerPage={itemsPerPage}
						totalItems={sortedTransactions.length}
						itemsPerPageOptions={[10, 25, 50, 100]}
						onNavigateTo={handlePageChange}
						onItemsPerPageChange={handleItemsPerPageChange}
						aria-label='Credit transactions pagination'
					/>
				)
			}
		/>
	);
}

export default UsageTable;
